<template>
  <div v-if="tab" class="configurator">
    <tab-type :tab="tab" @input="onInput" />

    <tab-configurator-general :tab="tab" @input="onInput" />
    <tab-configurator-categories :tab="tab" @input="onInput" />
    <tab-configurator-contributors :tab="tab" @input="onInput" />
    <tab-configurator-attachments :tab="tab" @input="onInput" />
    <tab-configurator-file-types :tab="tab" @input="onInput" />

    <configuration-tray-buttons
      :disabled="saving"
      :show-delete-button="isDeletable"
      @saved="save"
      @canceled="onCanceled"
      @deleted="confirmDeletion = true"
    />

    <confirmation-modal
      modal-id="modal-target-delete-tab"
      :show-modal="confirmDeletion"
      :confirmation="lang.get('tabs.alerts.delete')"
      :confirm-button-label="lang.get('buttons.delete')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmDeletion = false"
      @confirmed="remove"
    />

    <confirmation-modal
      modal-id="modal-target-cancel-tab-configuration"
      :show-modal="confirmCancellation"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmCancellation = false"
      @confirmed="cancel"
    />
  </div>
</template>

<script>
import _ from 'underscore';
import { mapActions, mapMutations, mapState } from 'vuex';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ConfigurationTrayButtons from '../ConfigurationTrayButtons';
import TabConfiguratorGeneral from './TabConfiguratorGeneral';
import TabConfiguratorCategories from './TabConfiguratorCategories';
import TabConfiguratorContributors from './TabConfiguratorContributors';
import TabConfiguratorAttachments from './TabConfiguratorAttachments';
import TabConfiguratorFileTypes from './TabConfiguratorFileTypes';
import TabType from './TabType';

export default {
  inject: ['lang'],
  components: {
    ConfigurationTrayButtons,
    ConfirmationModal,
    TabConfiguratorGeneral,
    TabConfiguratorCategories,
    TabConfiguratorContributors,
    TabConfiguratorAttachments,
    TabConfiguratorFileTypes,
    TabType
  },
  props: {
    tab: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmCancellation: false,
      confirmDeletion: false
    };
  },
  computed: {
    ...mapState('entryFormConfiguration', [
      'configurationInProgress',
      'saving'
    ]),
    isDeletable() {
      return this.tab.createdAt !== null && this.tab.type !== 'Details';
    }
  },
  created() {
    if (this.tab.createdAt) {
      this.preserveTab({ ...this.tab });
    }

    this.debouncedUpdate = _.debounce(this.updateTab, 300);

    this.$emit('created');
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'configureTab',
      'addTab',
      'saveTab',
      'deleteTab'
    ]),
    ...mapMutations('entryForm', ['updateTab', 'removeTab']),
    ...mapMutations('entryFormConfiguration', [
      'preserveTab',
      'setConfigurationInProgressFlag'
    ]),
    onInput(input) {
      if (!this.configurationInProgress) {
        this.setConfigurationInProgressFlag(true);
      }

      this.debouncedUpdate({ ...this.tab, ...input });
    },
    save() {
      if (!this.tab.createdAt) {
        this.addTab({ tab: this.tab });
      } else {
        this.saveTab({ tab: this.tab });
      }
    },
    onCanceled() {
      if (this.configurationInProgress) {
        this.confirmCancellation = true;
      } else {
        this.cancel();
      }
    },
    cancel() {
      this.confirmCancellation = false;
      this.$nextTick(() => {
        this.configureTab({ slug: null });
      });
    },
    remove() {
      this.confirmDeletion = false;

      this.deleteTab({ tab: this.tab }).then(response => {
        if (response && response.status === 200) {
          this.configureTab({ slug: null });
          this.removeTab(this.tab.slug);
        }
      });
    }
  }
};
</script>

<style scoped>
.configurator {
  margin-bottom: 150px;
}
</style>
